import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  formatInteger,
  formatMoney,
  formatPercent,
  formatSignedMoney,
  formatUtcClock,
  formatUtcStamp,
} from '../../shared/format.ts';
import { fetchJson } from '../lib/api.ts';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { BalanceHistoryChart } from '../components/BalanceHistoryChart.tsx';
import { ConfigHistoryDrawer, StrategyConfigEditor } from '../components/StrategyConfigEditor.tsx';
import { formatStrategyLabel } from '../../simulation/balance-monitor.ts';
import type { SimulationReportSnapshot } from '../../simulation/report-snapshot.ts';

type SnapshotResponse = {
  paperSimulation: true;
  reportingMode?: boolean;
  freshness?: string;
  published?: boolean;
  snapshot: SimulationReportSnapshot;
};

type FreshRunPreview = {
  allowed: boolean;
  reason: string | null;
  collectorHealthy: boolean;
  oldRun: {
    runId: string | null;
    status: string | null;
    lastProcessedSequence: number | null;
    balance: number | null;
    pnl: number | null;
  };
  newRun: {
    startingBalance: number;
    strategyCount: number;
    strategies: Array<{
      id: string;
      targetMultiplier: number;
      entryDrought: number;
      stopDrought: number;
      stakingMode: string;
      baseStake: number;
    }>;
    boundarySequence: number | null;
    expectedStartSequence: number | null;
  };
};

type RunHistoryRow = {
  runId: string;
  shortId: string;
  createdAtUtc: string;
  startedAtUtc: string | null;
  endedAtUtc: string | null;
  status: string;
  startingBalance: number;
  balance: number | null;
  pnl: number | null;
  startSequence: number;
  lastProcessedSequence: number | null;
  active: boolean;
};

export function PaperSimulationPage() {
  const [snapshot, setSnapshot] = useState<SimulationReportSnapshot | null>(null);
  const [freshness, setFreshness] = useState<string>('unavailable');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [runHistoryOpen, setRunHistoryOpen] = useState(false);
  const [freshPreview, setFreshPreview] = useState<FreshRunPreview | null>(null);
  const [freshError, setFreshError] = useState<string | null>(null);
  const [freshBusy, setFreshBusy] = useState(false);

  const applyPayload = useCallback((payload: SnapshotResponse, refreshedAt?: string) => {
    setSnapshot(payload.snapshot);
    setFreshness(payload.freshness ?? (payload.snapshot.stale ? 'stale' : 'published'));
    setError(null);
    if (refreshedAt) {
      setLastRefreshedAt(refreshedAt);
    }
  }, []);

  const loadPublished = useCallback(async () => {
    const payload = await fetchJson<SnapshotResponse>('/api/paper/snapshot');
    applyPayload(payload);
  }, [applyPayload]);

  useEffect(() => {
    let cancelled = false;
    void loadPublished().catch((cause: unknown) => {
      if (!cancelled) {
        setError(cause instanceof Error ? cause.message : 'Failed to load paper simulation');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loadPublished]);

  useEffect(() => {
    if (typeof EventSource === 'undefined') {
      return;
    }
    const source = new EventSource('/api/paper/events');
    source.addEventListener('snapshot', () => {
      void loadPublished().catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Failed to load published snapshot');
      });
    });
    source.onerror = () => {
      // EventSource reconnects on its own. Do not poll the simulation.
    };
    return () => {
      source.close();
    };
  }, [loadPublished]);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      const payload = await fetchJson<SnapshotResponse>('/api/paper/refresh', { method: 'POST' });
      applyPayload(payload, new Date().toISOString());
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause.message : 'Paper report refresh failed');
    } finally {
      setBusy(false);
    }
  }

  async function openEditor() {
    setEditorOpen(true);
  }

  async function openFreshRun() {
    setFreshError(null);
    setFreshBusy(true);
    try {
      const preview = await fetchJson<FreshRunPreview>('/api/paper/fresh-run/preview');
      setFreshPreview(preview);
    } catch (cause: unknown) {
      setFreshError(cause instanceof Error ? cause.message : 'Failed to load Start Fresh Run preview');
    } finally {
      setFreshBusy(false);
    }
  }

  async function confirmFreshRun() {
    setFreshBusy(true);
    setFreshError(null);
    try {
      await fetchJson('/api/paper/fresh-run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: 'START_FORWARD_PAPER' }),
      });
      setFreshPreview(null);
      await loadPublished();
    } catch (cause: unknown) {
      setFreshError(cause instanceof Error ? cause.message : 'Failed to start a new paper run');
    } finally {
      setFreshBusy(false);
    }
  }

  if (error && !snapshot) {
    return <StatusMessage kind="error">{error}</StatusMessage>;
  }
  if (!snapshot) {
    return <StatusMessage kind="loading">Loading paper simulation</StatusMessage>;
  }

  const unavailable = snapshot.status === 'UNKNOWN' || snapshot.version === 0;
  const stale = Boolean(snapshot.stale) || freshness === 'stale' || (freshness === 'unavailable' && !unavailable);
  const statusLabel = unavailable ? 'REPORT UNAVAILABLE' : snapshot.status;
  const starting = snapshot.wallet?.startingBalance ?? 500;
  const balance = snapshot.balance ?? snapshot.experimentBalance;
  const pnl = snapshot.pnl ?? snapshot.experimentPnl;
  const lowest = snapshot.lowestBalance;
  const peak = snapshot.peakBalance;
  const currentDd = snapshot.currentDrawdown;
  const maxDd = snapshot.maxDrawdown;
  const maxDeficit = snapshot.maxDeficit ?? 0;
  const monitor = snapshot.monitor;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">Paper simulation</p>
          <h1 className="mt-1 text-lg font-semibold">PAPER SIMULATION</h1>
          <p className="mt-1 text-sm font-medium">FORWARD EXPERIMENT</p>
          <p className="mt-1 text-sm text-muted">
            Refresh is reporting-only. Strategy edits apply after the current last processed round and do not rewrite history.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-lg font-semibold tracking-wide">{statusLabel}</p>
          <button
            type="button"
            className="rounded border border-accent bg-accent/20 px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            disabled={busy || freshBusy}
            onClick={() => void openFreshRun()}
          >
            Start Fresh Run
          </button>
          <button
            type="button"
            className="rounded border border-line px-3 py-1.5 text-sm font-semibold"
            onClick={() => setRunHistoryOpen(true)}
          >
            Run history
          </button>
          <button
            type="button"
            className="rounded border border-accent bg-accent/20 px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
            disabled={busy}
            onClick={() => void refresh()}
          >
            Refresh
          </button>
        </div>
      </header>

      {error ? <StatusMessage kind="error">{error}</StatusMessage> : null}
      {stale && !unavailable ? (
        <p className="rounded border border-warn/50 bg-panel px-3 py-2 text-sm text-warn" role="status">
          STALE / last successfully refreshed at {formatUtcStamp(snapshot.generatedAtUtc)}
          {snapshot.staleReason ? ` — ${snapshot.staleReason}` : ''}
        </p>
      ) : null}
      {unavailable ? (
        <p className="rounded border border-warn/50 bg-panel px-3 py-2 text-sm text-warn" role="status">
          REPORT UNAVAILABLE
        </p>
      ) : null}
      {snapshot.fault && snapshot.fault.code !== 'REPORT_UNAVAILABLE' ? (
        <p className="text-sm text-negative" role="alert">
          {snapshot.fault.code}: {snapshot.fault.message}
        </p>
      ) : null}

      <section className="rounded border border-line bg-panel p-5">
        <div className="grid gap-6 sm:grid-cols-2">
          <HeroMetric label="Balance" value={formatMoney(balance)} tone={tone(balance)} />
          <HeroMetric label="P&L" value={formatSignedMoney(pnl)} tone={tone(pnl)} />
        </div>
        <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          <Metric label="Starting" value={formatMoney(starting)} />
          <Metric label="Lowest" value={formatMoney(lowest)} />
          <Metric label="Peak" value={formatMoney(peak)} />
          <Metric label="Current DD" value={formatMoney(currentDd)} />
          <Metric label="Max DD" value={formatMoney(maxDd)} />
          <Metric label="Rounds" value={formatInteger(snapshot.roundsProcessed)} />
          <Metric label="Last round" value={formatInteger(snapshot.lastProcessedSequence)} />
          <Metric label="Started" value={formatUtcStamp(snapshot.startedAtUtc)} />
          {maxDeficit > 0 ? <Metric label="Max deficit" value={formatMoney(maxDeficit)} /> : null}
        </dl>
        <p className="mt-4 text-xs text-muted">
          {`Run ${snapshot.runId ?? '—'} · Start sequence ${formatInteger(snapshot.startSequence)} · Last refreshed: ${lastRefreshedAt ? formatUtcStamp(lastRefreshedAt) : 'not yet on this page'}`}
        </p>
      </section>

      <section className="rounded border border-line bg-panel p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Balance history</h2>
        <div className="mt-3">
          <BalanceHistoryChart
            series={monitor?.series ?? []}
            markers={monitor?.markers ?? []}
            startingBalance={starting}
          />
        </div>
      </section>

      <section className="rounded border border-line bg-panel">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Strategies</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border border-line px-2 py-1 text-xs font-semibold"
              onClick={() => setHistoryOpen(true)}
            >
              Config history
            </button>
            <button
              type="button"
              className="rounded border border-accent bg-accent/20 px-2 py-1 text-xs font-semibold"
              onClick={() => void openEditor()}
            >
              Edit strategies
            </button>
          </div>
        </div>
        {snapshot.config?.pendingVersion ? (
          <p className="border-b border-line px-4 py-2 text-sm text-warn" role="status">
            Config v{snapshot.config.pendingVersion} pending. Effective after seq {formatInteger(snapshot.config.pendingEffectiveAfter)}.
          </p>
        ) : snapshot.config ? (
          <p className="border-b border-line px-4 py-2 text-xs text-muted">
            CONFIG v{snapshot.config.activeVersion} ACTIVE
            {snapshot.config.activeEffectiveFrom != null ? ` · Since seq ${formatInteger(snapshot.config.activeEffectiveFrom)}` : ''}
            {` · Configured: ${snapshot.configuredStrategies ?? snapshot.strategies.filter((row) => row.lifecycle !== 'RETIRING' && row.lifecycle !== 'RETIRING_FOR_REPLACEMENT').length}`}
            {` · Retiring: ${snapshot.retiringStrategies ?? snapshot.strategies.filter((row) => row.lifecycle === 'RETIRING' || row.lifecycle === 'RETIRING_FOR_REPLACEMENT').length}`}
            {` · Waiting reset: ${snapshot.waitingResetStrategies ?? snapshot.strategies.filter((row) => row.lifecycle === 'WAIT_FOR_RESET').length}`}
          </p>
        ) : null}
        <div className="table-scroll">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-soft text-xs uppercase tracking-wide text-muted">
              <tr>
                <Th>Strategy</Th>
                <Th>State</Th>
                <Th>Current Drought</Th>
                <Th>Next Stake</Th>
                <Th>Episodes</Th>
                <Th>Wins</Th>
                <Th>Stopouts</Th>
                <Th>Wagers</Th>
                <Th>P&L</Th>
                <Th>ROI</Th>
              </tr>
            </thead>
            <tbody>
              {snapshot.strategies.length === 0 ? (
                <tr>
                  <td className="px-3 py-4 text-muted" colSpan={10}>
                    No published paper-simulation snapshot yet. Press Refresh or send Telegram /sim.
                  </td>
                </tr>
              ) : (
                snapshot.strategies.map((strategy) => {
                  const stats = monitor?.strategies[strategy.id];
                  const wins = stats?.wins ?? Math.max(0, strategy.successfulCompletions - strategy.inheritedCompletions);
                  const stopouts = stats?.stopouts ?? strategy.stopOuts;
                  const episodes = stats?.episodes ?? wins + stopouts;
                  const wagers = stats?.wagers ?? strategy.totalWagers;
                  const strategyPnl = stats?.pnl ?? strategy.experimentPnl;
                  const roi = stats?.roi ?? strategy.experimentRoi;
                  return (
                    <tr key={strategy.id} className={`border-t border-line ${strategy.phase === 'betting' ? 'bg-accent/10' : ''}`}>
                      <Td>{formatStrategyLabel(strategy)}</Td>
                      <Td>{strategy.lifecycle && strategy.lifecycle !== 'ACTIVE' ? `${strategy.phase} / ${strategy.lifecycle}` : strategy.phase}</Td>
                      <Td>{formatInteger(strategy.currentTargetDrought)}</Td>
                      <Td>{formatMoney(strategy.currentStake)}</Td>
                      <Td>{formatInteger(episodes)}</Td>
                      <Td>{formatInteger(wins)}</Td>
                      <Td>{formatInteger(stopouts)}</Td>
                      <Td>{formatInteger(wagers)}</Td>
                      <Td className={tone(strategyPnl)}>{formatSignedMoney(strategyPnl)}</Td>
                      <Td>{roi === null ? '—' : formatPercent(roi, 1)}</Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded border border-line bg-panel">
        <div className="border-b border-line px-4 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Recent events</h2>
        </div>
        {(monitor?.recentEvents.length ?? 0) === 0 ? (
          <p className="px-4 py-4 text-sm text-muted">No major completed episodes yet in this report.</p>
        ) : (
          <ul className="divide-y divide-line text-sm">
            {monitor!.recentEvents.map((event) => (
              <li key={`${event.kind}-${event.sequence}-${event.label}`} className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-2">
                <span className="w-14 text-muted">{formatUtcClock(event.timestampUtc)}</span>
                <span className="min-w-28 font-medium">{event.label}</span>
                <span className="w-24 text-muted">{event.detail}</span>
                <span className={tone(event.amount)}>{event.amount == null ? '—' : event.kind === 'STOP' || event.kind === 'SUCCESS' ? formatSignedMoney(event.amount) : formatMoney(event.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <StrategyConfigEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onApplied={async () => {
          await loadPublished();
        }}
      />
      <ConfigHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <RunHistoryDrawer open={runHistoryOpen} onClose={() => setRunHistoryOpen(false)} />
      {freshPreview ? (
        <div className="fixed inset-0 z-30 flex items-start justify-center overflow-auto bg-black/40 p-4">
          <div className="w-full max-w-lg rounded border border-line bg-panel p-4 shadow-lg">
            <h2 className="text-sm font-semibold">Start Fresh Run</h2>
            {freshError ? <p className="mt-2 text-sm text-negative">{freshError}</p> : null}
            {!freshPreview.collectorHealthy || !freshPreview.allowed ? (
              <p className="mt-3 text-sm text-warn">
                {freshPreview.reason ?? 'Collector is not currently healthy. Restore collection before starting a new paper run.'}
              </p>
            ) : (
              <div className="mt-3 space-y-3 text-sm">
                <div>
                  <p className="font-semibold">Old run</p>
                  <p className="text-muted">
                    {freshPreview.oldRun.runId} · {freshPreview.oldRun.status} · last seq{' '}
                    {formatInteger(freshPreview.oldRun.lastProcessedSequence)} · Balance {formatMoney(freshPreview.oldRun.balance)} · P&L{' '}
                    {formatSignedMoney(freshPreview.oldRun.pnl)}
                  </p>
                  {freshPreview.oldRun.status === 'RUNNING' || freshPreview.oldRun.status === 'PAUSED' ? (
                    <p className="mt-1 text-xs text-warn">
                      This stops the current experiment and keeps it in Run history. It does not rewrite old wagers.
                    </p>
                  ) : null}
                </div>
                <div>
                  <p className="font-semibold">New run</p>
                  <p className="text-muted">
                    Starting {formatMoney(freshPreview.newRun.startingBalance)} · {formatInteger(freshPreview.newRun.strategyCount)} strategies
                    · boundary seq {formatInteger(freshPreview.newRun.boundarySequence)} · start seq{' '}
                    {formatInteger(freshPreview.newRun.expectedStartSequence)}
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-muted">
                    {freshPreview.newRun.strategies.map((row) => (
                      <li key={row.id}>
                        {row.id}: {row.targetMultiplier}x D{row.entryDrought}-S{row.stopDrought} {row.stakingMode} ${row.baseStake}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded border border-line px-3 py-1.5 text-sm" onClick={() => setFreshPreview(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="rounded border border-accent bg-accent/20 px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
                disabled={freshBusy || !freshPreview.allowed}
                onClick={() => void confirmFreshRun()}
              >
                Start New Run
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RunHistoryDrawer(props: { open: boolean; onClose: () => void }) {
  const [rows, setRows] = useState<RunHistoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.open) {
      return;
    }
    void fetchJson<{ runs: RunHistoryRow[] }>('/api/paper/runs')
      .then((payload) => {
        setRows(payload.runs ?? []);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Failed to load run history');
      });
  }, [props.open]);

  if (!props.open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-20 flex items-start justify-center overflow-auto bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded border border-line bg-panel p-4 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-sm font-semibold">Run history</h2>
          <button type="button" className="text-sm" onClick={props.onClose}>
            Close
          </button>
        </div>
        {error ? <p className="mt-3 text-sm text-negative">{error}</p> : null}
        <div className="mt-3 table-scroll">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-2 py-1">Run</th>
                <th className="px-2 py-1">Status</th>
                <th className="px-2 py-1">Start</th>
                <th className="px-2 py-1">End</th>
                <th className="px-2 py-1">Starting</th>
                <th className="px-2 py-1">Balance</th>
                <th className="px-2 py-1">P&L</th>
                <th className="px-2 py-1">Seq</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.runId} className={`border-t border-line ${row.active ? 'bg-accent/10' : ''}`}>
                  <td className="px-2 py-1 font-medium" title={row.runId}>
                    {row.shortId}
                    {row.active ? ' · active' : ''}
                  </td>
                  <td className="px-2 py-1">{row.status}</td>
                  <td className="px-2 py-1">{formatUtcStamp(row.startedAtUtc ?? row.createdAtUtc)}</td>
                  <td className="px-2 py-1">{formatUtcStamp(row.endedAtUtc)}</td>
                  <td className="px-2 py-1">{formatMoney(row.startingBalance)}</td>
                  <td className="px-2 py-1">{formatMoney(row.balance)}</td>
                  <td className={`px-2 py-1 ${tone(row.pnl)}`}>{formatSignedMoney(row.pnl)}</td>
                  <td className="px-2 py-1">
                    {formatInteger(row.startSequence)}–{formatInteger(row.lastProcessedSequence)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function tone(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value) || value === 0) {
    return '';
  }
  return value < 0 ? 'text-negative' : 'text-positive';
}

function HeroMetric(props: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">{props.label}</p>
      <p className={`mt-1 text-4xl font-semibold tabular-nums ${props.tone ?? ''}`}>{props.value}</p>
    </div>
  );
}

function Metric(props: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{props.label}</dt>
      <dd className="text-sm font-medium">{props.value}</dd>
    </div>
  );
}

function Th(props: { children: string }) {
  return <th className="whitespace-nowrap px-3 py-2 font-medium">{props.children}</th>;
}

function Td(props: { children: ReactNode; className?: string }) {
  return <td className={`whitespace-nowrap px-3 py-2 ${props.className ?? ''}`}>{props.children}</td>;
}
